import 'react-day-picker/style.css';
import moment from 'moment';
import { FC, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import { DateRange, DayPicker } from 'react-day-picker';
import styles from './datePickerV3.module.css';
import { DatePickerV3Props } from './datePickerV3.types';
import CalendarIcon from '../../assets/calendar.svg';

const formatters = {
  formatWeekdayName: (date: Date) => {
    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][date.getDay()];
  },
};

const DatePickerV3: FC<DatePickerV3Props> = (props) => {
  const { mode, value, onChange, showNavigationArrows = true } = props;
  const isSingle: boolean = mode === 'single';

  const [open, setOpen] = useState<boolean>(false);
  const [tempRange, setTempRange] = useState<DateRange | undefined>(value);
  const [month, setMonth] = useState<Date>(value?.from ?? new Date());

  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );

  let placement: 'bottom-start' | 'bottom-end' | 'bottom' = 'bottom';
  if (props.position === 'left') placement = 'bottom-start';
  else if (props.position === 'right') placement = 'bottom-end';

  const { styles: popperStyles, attributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement,
      modifiers: [
        { name: 'offset', options: { offset: [0, 6] } },
        { name: 'preventOverflow', options: { boundary: 'clippingParents' } },
      ],
    },
  );

  useEffect(() => {
    setTempRange(value);
    if (value?.from) setMonth(value.from);
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !referenceElement?.contains(target) &&
        !popperElement?.contains(target)
      ) {
        setOpen(false);
        setTempRange(value);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, referenceElement, popperElement, value]);

  const handleApply = () => {
    if (tempRange?.from) {
      const applied: DateRange = {
        from: tempRange.from,
        to: tempRange.to ?? tempRange.from,
      };
      onChange(applied);
    }
    setOpen(false);
  };

  const handleCancel = () => {
    setTempRange(value);
    setOpen(false);
  };

  const handleArrowNav = (direction: number) => {
    if (!value?.from) return;
    const today: Date = new Date();
    today.setHours(23, 59, 59, 999);
    if (isSingle) {
      const newDate: Date = moment(value.from).add(direction, 'day').toDate();
      if (newDate > today) return;
      onChange({ from: newDate, to: newDate });
    } else {
      // Range: left (direction -1) goes earlier from start, right (direction +1) goes later from end
      const baseDate = direction < 0 ? value.from : (value.to ?? value.from);
      const newDate: Date = moment(baseDate).add(direction, 'day').toDate();
      if (newDate > today) return;
      onChange({ from: newDate, to: newDate });
    }
  };

  const isRightArrowDisabled = (): boolean => {
    if (!value?.from) return true;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const baseDate = isSingle ? value.from : (value.to ?? value.from);
    return moment(baseDate).add(1, 'day').toDate() > today;
  };

  const goToPrevMonth = () =>
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1));
  const goToNextMonth = () =>
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1));

  const getDisplayText = (): string => {
    if (isSingle) {
      return value?.from
        ? moment(value.from).format('DD/MM/YYYY')
        : 'DD/MM/YYYY';
    }
    if (!value?.from) return 'DD/MM/YYYY — DD/MM/YYYY';
    const fromText: string = moment(value.from).format('DD/MM/YYYY');
    const toText: string = value.to
      ? moment(value.to).format('DD/MM/YYYY')
      : 'DD/MM/YYYY';
    return `${fromText} — ${toText}`;
  };

  const displayText: string = getDisplayText();

  return (
    <div
      ref={setReferenceElement}
      className={`relative inline-block ${props.className ?? ''}`}
    >
      <button
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        {showNavigationArrows && (
          <button
            className={styles.navArrow}
            onClick={(e) => {
              e.stopPropagation();
              handleArrowNav(-1);
            }}
          >
            ‹
          </button>
        )}
        <img src={CalendarIcon} alt="" className={styles.calendarIcon} />
        <span>{displayText}</span>
        {showNavigationArrows && (
          <button
            className={`${styles.navArrow} ${isRightArrowDisabled() ? styles.navArrowDisabled : ''}`}
            disabled={isRightArrowDisabled()}
            onClick={(e) => {
              e.stopPropagation();
              handleArrowNav(1);
            }}
          >
            ›
          </button>
        )}
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={setPopperElement}
            style={{
              ...popperStyles.popper,
              zIndex: 9999,
              visibility: popperStyles.popper.transform ? 'visible' : 'hidden',
            }}
            {...attributes.popper}
          >
            <div className={styles.dropdown}>
              <div className={styles.monthHeader}>
                <button className={styles.monthNav} onClick={goToPrevMonth}>
                  ‹
                </button>
                <span className={styles.monthLabel}>
                  {moment(month).format('MMMM YYYY')}
                </span>
                <button className={styles.monthNav} onClick={goToNextMonth}>
                  ›
                </button>
              </div>

              <div className={styles.calendar}>
                {isSingle ? (
                  <DayPicker
                    mode="single"
                    selected={tempRange?.from}
                    onSelect={(date: Date | undefined) =>
                      setTempRange(date ? { from: date, to: date } : undefined)
                    }
                    month={month}
                    onMonthChange={setMonth}
                    weekStartsOn={1}
                    showOutsideDays={false}
                    disabled={{ after: new Date() }}
                    formatters={formatters}
                    classNames={{
                      selected: styles.selectedSingle,
                    }}
                  />
                ) : (
                  <DayPicker
                    mode="range"
                    selected={tempRange}
                    onSelect={(range: DateRange | undefined) =>
                      setTempRange(range)
                    }
                    month={month}
                    onMonthChange={setMonth}
                    weekStartsOn={1}
                    showOutsideDays={false}
                    disabled={{ after: new Date() }}
                    formatters={formatters}
                    classNames={{
                      range_start: styles.rangeStart,
                      range_end: styles.rangeEnd,
                      range_middle: styles.rangeMiddle,
                      selected: styles.selected,
                    }}
                  />
                )}
              </div>

              <div className={styles.footer}>
                <button className={styles.cancelBtn} onClick={handleCancel}>
                  Cancel
                </button>
                <button
                  className={styles.applyBtn}
                  onClick={handleApply}
                  disabled={!tempRange?.from}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DatePickerV3;
